/**
 * Derive which manual-filter fastener-brand chips an application's fastener
 * lines should light up — the app-driven (Option-B) filter (issue 7).
 *
 * Mirrors the POC `applyAppDrivenFilters` substring
 * matching, with the AUTORIV correction:
 *
 * - AUTORIV is a first-class APPLICATION brand that resolves to the
 * `supports_other` product family (see `FASTENER_BRAND_PWKC_COLUMN` in
 * @penn/shared-types) — the SAME family as OTHER, NOT SHEREX. It has no
 * manual filter chip of its own, so an Autoriv-bearing application lights
 * up the "Other" chip (code `OTHER`).
 * - "AUTORIV" contains the substring "RIV", so it MUST be detected before
 * the blind-rivet (SHEREX) rule, otherwise it would be mis-mapped to
 * SHEREX. Previously there was no AUTORIV branch at all, so when another
 * brand (e.g. PROFIL) already populated the set, the Autoriv line was
 * silently dropped (the `OTHER` fallback only fires for an EMPTY set) —
 * that was the reported bug.
 *
 * Returned values are filter-bar chip codes (`PEM` / `SHEREX` / `PROFIL` /
 * `OTHER`) — the four codes the customer selector renders (there is no AUTORIV
 * chip). The FilterPanel highlights every chip whose code is in this list and
 * sends them as the selector-eval `fastenerBrand` (a union when >1).
 */
export interface AppFastenerLike {
  brand?: string | null;
  type?: string | null;
}

export function deriveAppDrivenBrands(
  fasteners: ReadonlyArray<AppFastenerLike>,
): string[] {
  const brands = new Set<string>();
  let any = false;

  for (const f of fasteners) {
    const brand = (f.brand ?? "").toUpperCase();
    const type = (f.type ?? "").toUpperCase();
    if (brand || type) any = true;

    if (brand.includes("PROFIL") || type.includes("PROFIL")) brands.add("PROFIL");
    if (brand.includes("PEM") || brand.includes("PAM") || brand.includes("PENN")) {
      brands.add("PEM");
    }

    const isAutoriv = brand.includes("AUTORIV") || type.includes("AUTORIV");
    if (isAutoriv) brands.add("OTHER");
    if (!isAutoriv && (brand.includes("SHEREX") || type.includes("RIV"))) {
      brands.add("SHEREX");
    }
  }

  // Any recognised-but-unmapped fastener (e.g. brand "OTHER", or a free-text
  // line) still surfaces SOMETHING — the POC's catch-all "Other fasteners".
  if (any && brands.size === 0) brands.add("OTHER");

  return Array.from(brands);
}
